import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Camera, ImageIcon, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { pickFile, releasePreview, type PickedFile } from '@/lib/media';
import AuthLayout from './AuthLayout';
import styles from './auth.module.css';

type AccountType = 'client' | 'provider';

export default function Signup() {
  const navigate = useNavigate();
  // The landing's "Devenir prestataire" links here with ?type=provider.
  const [searchParams] = useSearchParams();
  const [accountType, setAccountType] = useState<AccountType>(
    searchParams.get('type') === 'provider' ? 'provider' : 'client'
  );
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [cin, setCin] = useState('');
  const [cinPhoto, setCinPhoto] = useState<PickedFile | null>(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => () => releasePreview(cinPhoto?.url), [cinPhoto]);

  const choosePhoto = async (source: 'camera' | 'gallery') => {
    setShowPhotoOptions(false);
    const picked = await pickFile('image', source);
    if (picked) {
      releasePreview(cinPhoto?.url);
      setCinPhoto(picked);
    }
  };

  const clearPhoto = () => {
    releasePreview(cinPhoto?.url);
    setCinPhoto(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (!firstName || !lastName || !email || !phone || !password || !confirmPassword) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    if (accountType === 'provider' && !cin) {
      setError('Veuillez saisir votre numéro de CIN.');
      return;
    }
    if (accountType === 'provider' && !cinPhoto) {
      setError('Veuillez ajouter une photo de votre CIN.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    setLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
      });

      if (authError) {
        setError(authError.message);
        return;
      }
      if (!authData.user) {
        setError('Compte créé. Vérifiez votre email pour confirmer votre inscription.');
        return;
      }

      let cinPhotoUrl: string | null = null;
      if (accountType === 'provider' && cinPhoto) {
        const filePath = `${authData.user.id}/cin.${cinPhoto.extension}`;
        const { error: uploadError } = await supabase.storage
          .from('cin-photos')
          .upload(filePath, cinPhoto.file, {
            contentType: cinPhoto.mimeType,
            upsert: true,
          });

        if (uploadError) {
          setError(`Erreur lors du téléchargement de la photo CIN : ${uploadError.message}`);
          return;
        }

        cinPhotoUrl = supabase.storage.from('cin-photos').getPublicUrl(filePath).data.publicUrl;
      }

      const { error: profileError } = await supabase.from('profiles').insert({
        id: authData.user.id,
        full_name: `${firstName.trim()} ${lastName.trim()}`,
        phone: phone.trim(),
        city: '',
        account_type: accountType,
        avatar_url: null,
        cin: accountType === 'provider' ? cin.trim() : null,
        cin_photo_url: cinPhotoUrl,
      });

      if (profileError) {
        setError(profileError.message);
        return;
      }

      navigate(accountType === 'provider' ? '/provider-request' : '/app', { replace: true });
    } catch {
      setError('Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Créer un compte"
      subtitle="Rejoignez DABA en tant que client ou prestataire."
      footer={
        <>
          Vous avez déjà un compte ? <Link to="/login">Connectez-vous</Link>
        </>
      }
    >
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {error ? (
          <p className="alert alert--error" role="alert">
            {error}
          </p>
        ) : null}

        <div className={styles.segmented} role="radiogroup" aria-label="Type de compte">
          {(['client', 'provider'] as const).map((type) => (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={accountType === type}
              className={
                accountType === type ? `${styles.segment} ${styles.segmentOn}` : styles.segment
              }
              onClick={() => setAccountType(type)}
              disabled={loading}
            >
              {type === 'client' ? 'Client' : 'Prestataire'}
            </button>
          ))}
        </div>

        <div className={styles.row}>
          <div className="field">
            <label className="label" htmlFor="lastName">
              Nom <span className="req">*</span>
            </label>
            <input
              id="lastName"
              className="input"
              autoComplete="family-name"
              placeholder="Dupont"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              disabled={loading}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="firstName">
              Prénom <span className="req">*</span>
            </label>
            <input
              id="firstName"
              className="input"
              autoComplete="given-name"
              placeholder="Jean"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="email">
            Email <span className="req">*</span>
          </label>
          <input
            id="email"
            className="input"
            type="email"
            autoComplete="email"
            placeholder="your@email.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={loading}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="phone">
            Numéro de téléphone <span className="req">*</span>
          </label>
          <input
            id="phone"
            className="input"
            type="tel"
            autoComplete="tel"
            placeholder="0612345678"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            disabled={loading}
          />
        </div>

        {accountType === 'provider' ? (
          <>
            <div className="field">
              <label className="label" htmlFor="cin">
                Numéro CIN <span className="req">*</span>
              </label>
              <input
                id="cin"
                className="input"
                placeholder="AB123456"
                value={cin}
                onChange={(event) => setCin(event.target.value.toUpperCase())}
                disabled={loading}
              />
            </div>

            <div className="field">
              <span className="label">
                Photo CIN <span className="req">*</span>
              </span>
              {cinPhoto ? (
                <div className={styles.photoPreview}>
                  <img className={styles.photoPreviewImg} src={cinPhoto.url} alt="Aperçu CIN" />
                  <button
                    type="button"
                    className={styles.photoRemove}
                    onClick={clearPhoto}
                    aria-label="Retirer la photo"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : showPhotoOptions ? (
                <div className={styles.photoActions}>
                  <button
                    type="button"
                    className="btn btn--outline"
                    onClick={() => void choosePhoto('camera')}
                    disabled={loading}
                  >
                    <Camera size={18} /> Appareil photo
                  </button>
                  <button
                    type="button"
                    className="btn btn--outline"
                    onClick={() => void choosePhoto('gallery')}
                    disabled={loading}
                  >
                    <ImageIcon size={18} /> Fichier
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.photoPicker}
                  onClick={() => setShowPhotoOptions(true)}
                  disabled={loading}
                >
                  <Camera size={22} />
                  Choisir une photo
                </button>
              )}
            </div>
          </>
        ) : null}

        <div className="field">
          <label className="label" htmlFor="password">
            Mot de passe <span className="req">*</span>
          </label>
          <input
            id="password"
            className="input"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={loading}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="confirmPassword">
            Confirmer le mot de passe <span className="req">*</span>
          </label>
          <input
            id="confirmPassword"
            className="input"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            disabled={loading}
          />
        </div>

        <button type="submit" className="btn btn--primary btn--block" disabled={loading}>
          {loading ? 'Création en cours...' : 'Créer mon compte'}
        </button>
      </form>
    </AuthLayout>
  );
}
